import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Typography, TextField, Button, Stack, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress } from "@mui/material";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { onboardUser, getUserProfile } from "../../services/adminService";
import { getAssessmentLock, getLatestAssessment } from "../../services/assessmentService";

export default function AdminOnboardingPanel({ onSelectUser }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [confirmOnboard, setConfirmOnboard] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const snap = await getDocs(collection(db, "users"));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (!active) return;
        setUsers(list);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return users.filter(u => (`${u.firstName||""} ${u.lastName||""} ${u.email||""}`).toLowerCase().includes(s));
  }, [users, search]);

  const loadDetails = async (userId) => {
    setLoadingDetails(true);
    try {
      const profile = await getUserProfile(userId);
      const lock = await getAssessmentLock(userId);
      const last = await getLatestAssessment(userId);
      setDetails({
        profile,
        lock,
        assessment: last,
        progressPct: Math.min(100, Math.round(((profile?.totalCredits || 0) / 480) * 100))
      });
      onSelectUser && onSelectUser(profile);
    } catch (e) {
      setError(e.message || "Failed to load details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelect = (u) => {
    setSelected(u);
    setDetails(null);
    setError("");
    loadDetails(u.id);
  };

  const handleOnboard = async () => {
    setConfirmOnboard(false);
    try {
      await onboardUser("ADMIN", selected.id);
      await loadDetails(selected.id);
    } catch (e) {
      setError(e.message || "Onboard failed");
    }
  };

  return (
    <Card sx={{ borderTop: "6px solid #003366" }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>User Onboarding Management</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField fullWidth size="small" placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2, maxHeight: 420, overflowY: "auto" }}>
              {filtered.map(u => (
                <Box key={u.id} sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid #eee", mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{`${u.firstName||""} ${u.lastName||""}`.trim() || u.displayName || "Unknown"}</Typography>
                    <Typography variant="body2" color="text.secondary">{u.email}</Typography>
                  </Box>
                  <Button variant="outlined" onClick={() => handleSelect(u)}>View</Button>
                </Box>
              ))}
            </Box>

            <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2, minHeight: 320 }}>
              {!selected && (
                <Typography color="text.secondary">Select a user to view onboarding status.</Typography>
              )}
              {selected && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{selected.firstName} {selected.lastName}</Typography>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                  {loadingDetails ? (
                    <CircularProgress />
                  ) : details && (
                    <Box>
                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                        <Chip label={`Credits: ${details.profile?.totalCredits || 0}`} color="primary" />
                        <Chip label={details.profile?.dashboardUnlocked ? "Dashboard: Unlocked" : "Dashboard: Locked"} color={details.profile?.dashboardUnlocked ? "success" : "default"} />
                        <Chip label={`Assessment: ${details.lock?.locked ? "Locked" : "Unlocked"}`} color={details.lock?.locked ? "warning" : "success"} />
                        <Chip label={`Progress: ${details.progressPct}%`} />
                      </Stack>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Last Assessment: {details.assessment?.createdAt?.toDate ? details.assessment.createdAt.toDate().toLocaleString() : "N/A"} | Score: {details.assessment?.score ?? "-"}%
                      </Typography>
                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => setConfirmOnboard(true)}
                          sx={{ backgroundColor: "#C1272D", "&:hover": { backgroundColor: "#A51F25" } }}
                        >
                          Onboard User
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}

        <Dialog open={confirmOnboard} onClose={() => setConfirmOnboard(false)}>
          <DialogTitle>Confirm Onboard</DialogTitle>
          <DialogContent>
            <Typography>Reset assessment lock and set onboarding status to pending?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOnboard(false)}>Cancel</Button>
            <Button onClick={handleOnboard} variant="contained">Confirm</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
